import Marker from './interfaces/Marker'
import { EXCEPTION_MAP } from './Exceptions'
import moment from 'moment'
import 'moment-duration-format'
import { activeFiltersAND, filtersAND } from './components/Pages/types'
import i18n from 'live/services/i18n'
import { formatAddress, getReverseGeocoding } from './api/google-integration'

export const getLastUpdated = time => {
    const lastTime = moment(time, 'x')
    const now = moment()
    const duration = moment.duration(now.diff(lastTime))

    return duration.humanize()
}

export const capitalizeFirstLetter = (str: string): string => {
    if (!str || str.length === 0) {
        return str
    }

    return str.charAt(0).toUpperCase() + str.slice(1)
}

export const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp)
    const day = date.getDate()
    const month = date.getMonth() + 1 // Mês começa do zero
    const year = date.getFullYear()
    const hours = date.getHours()
    const minutes = date.getMinutes()

    return `${day.toString().padStart(2, '0')}/${month
        .toString()
        .padStart(2, '0')}/${year} - ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

export const getTimeElapsed = (timestamp: number): string => {
    const currentTime = Date.now()
    const differenceInMillis = currentTime - timestamp

    const seconds = Math.floor(differenceInMillis / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)

    return `${weeks === 0 ? '' : `${weeks}${i18n('s')} `}${days === 0 ? '' : `${days % 7}d `}${
        hours === 0 ? '' : `${hours % 24}h `
    }${minutes === 0 ? '0m' : `${minutes % 60}m`}`
}

export const navegateToPositionsReport = (licensePlate, positionsReportId) => {
    const lookupFilter = JSON.stringify({ "hw_imei_company.licensePlate_imei": licensePlate,})
    const DASHBOARD_EXPORT_LAST_TRIPS_ID = positionsReportId
    const url = `#/dashboard/${DASHBOARD_EXPORT_LAST_TRIPS_ID}/?mode=view&filter=${lookupFilter}`

    window.open(url, '_blank')
}


export const navegateToTripsReport = (collection, imei, tripsReportId) => {
    const lookupFilter = JSON.stringify({ identity_lookup_imei: imei, identity_lookup_collection: collection })
    const DASHBOARD_EXPORT_LAST_TRIPS_ID = tripsReportId
    const url = `#/dashboard/${DASHBOARD_EXPORT_LAST_TRIPS_ID}/?mode=view&filter=${lookupFilter}`

    window.open(url, '_blank')
}

export const getDuration = (start, end) => {
    const startTime = moment(start, 'x')
    const endTime = moment(end, 'x')
    const timeDiff = endTime.diff(startTime)

    const result = moment.duration(timeDiff, 'milliseconds').format('h[h] m[min]')

    return result.replace('mins', 'min')
}

export const getDistance = estimatedDistance => {
    return estimatedDistance ? (estimatedDistance / 1000).toFixed(2) : ''
}

export const getTime = estimatedTime => {
    if (!estimatedTime) {
        return ''
    } else {
        return moment.duration(estimatedTime, 'seconds').humanize()
    }
}

export const CAR_STATUS = {
    DISCONNECTED: 'disconnected',
    MOVING: 'moving',
    STOPPED: 'stopped',
    NOT_COMUNICATING: 'not_communicating'
}

export const filterHardwareData = (
    vehicles: { imei: number; [key: string]: unknown }[] = [],
    hardwareData: { [keyMatch: string]: Record<string, unknown> } = {}
): any => {
    const aux = {}

    for (const vehicle of vehicles) {
        const hardwareDataForImei = hardwareData?.[vehicle.imei]
        if (hardwareDataForImei) aux[vehicle.imei] = hardwareData[vehicle.imei]
    }

    return aux
}

export const findHardwareKeyBasedOnLookupName = (
    filtersAND: filtersAND[],
    activeFiltersAND: activeFiltersAND
): Map<string, string> => {
    const lookupToKeyMap = new Map<string, string>()

    for (const lookupName in activeFiltersAND) {
        const filter = filtersAND.find(filterAND => filterAND.lookup === lookupName)
        if (filter) lookupToKeyMap.set(lookupName, filter.key)
    }

    return lookupToKeyMap
}

export const filterHardwareAND = (
    filtersAND: filtersAND[],
    filtersANDActive: activeFiltersAND,
    hardwareData: { [keyMatch: string | number]: unknown }
) => {
    const lookupToKeyMap = findHardwareKeyBasedOnLookupName(filtersAND, filtersANDActive)
    for (const keyMatch in hardwareData) {
        for (const activeFilter in filtersANDActive) {
            const dataKey = lookupToKeyMap.get(activeFilter)
            const allValuesForAnActiveFilter = filtersANDActive[activeFilter].map(filter => filter.value)
            const satisfiesFilter = allValuesForAnActiveFilter.includes(String(hardwareData[keyMatch]?.[dataKey]))

            if (!satisfiesFilter) delete hardwareData[keyMatch]
        }
    }

    return hardwareData
}

export const sort = (key: string, trips: any[] = []): any[] => {
    if (trips === null || trips.length === 0) return trips

    let val = 1
    const aux = [...trips]

    if (key && key[0] === '-') {
        val = -val
        key = key.substring(1, key.length)
    }

    const sortable = (a, b) => {
        const eleA = a[key]
        const eleB = b[key]

        if (eleA > eleB) return val
        if (eleA < eleB) return -val

        return 0
    }

    return aux.sort(sortable)
}

export const sortAsc = (path: string, list: any[] = [], status): any[] => {
    const sortable = (a, b) => {
        if (status && a?.sortKeys?.car_status === status) a.precedence = 10
        if (status && b?.sortKeys?.car_status === status) b.precedence = 10
        const eleA = a?.sortKeys?.[path]
        const eleB = b?.sortKeys?.[path]
        if (a.precedence === b.precedence) {
            if (!eleA) return 1
            if (!eleB) return -1
            if (eleA > eleB) return 1
            if (eleA < eleB) return -1

            return 0
        } else if (a.precedence > b.precedence) {
            return -1
        } else if (a.precedence < b.precedence) {
            return 1
        }

        return 0
    }

    return list.sort(sortable)
}

export const sortDesc = (path: string, list: any[] = [], status): any[] => {
    const sortable = (a, b) => {
        if (status && a?.sortKeys?.car_status === status) a.precedence = 10
        if (status && b?.sortKeys?.car_status === status) b.precedence = 10
        const eleA = a?.sortKeys?.[path]
        const eleB = b?.sortKeys?.[path]
        if (a.precedence === b.precedence) {
            if (!eleA) return 1
            if (!eleB) return -1
            if (eleA > eleB) return -1
            if (eleA < eleB) return 1

            return 0
        } else if (a.precedence > b.precedence) {
            return -1
        } else if (a.precedence < b.precedence) {
            return 1
        }

        return 0
    }

    return list.sort(sortable)
}

export const getCarStatusStyle = (carStatus: string) => {
    const style: any = { fontSize: '10px' }

    if (carStatus === CAR_STATUS.DISCONNECTED) {
        style.color = '#606A80'
    } else if (carStatus === CAR_STATUS.MOVING) {
        style.color = '#01C292'
    } else if (carStatus === CAR_STATUS.STOPPED) {
        style.color = '#F89300'
    } else if (carStatus === CAR_STATUS.NOT_COMUNICATING) {
        style.color = '#868695'
    }

    return style
}
export const getCarStatusLegendStyle = (carStatus: string): StyleSheet => {
    const style: any = { fontSize: '10px' }

    if (carStatus === CAR_STATUS.DISCONNECTED) {
        style.background = '#606A80'
    } else if (carStatus === CAR_STATUS.MOVING) {
        style.background = '#01C292'
    } else if (carStatus === CAR_STATUS.STOPPED) {
        style.background = '#F89300'
    } else if (carStatus === CAR_STATUS.NOT_COMUNICATING) {
        style.background = '#868695'
    }

    return style
}

export const getCarStatusIcon = (carStatus: string) => {
    if (carStatus === CAR_STATUS.MOVING) return '/content/plugin-widget-gmaps/green-car.png'
    if (carStatus === CAR_STATUS.STOPPED) return '/content/plugin-widget-gmaps/orange-car.png'
    if (carStatus === CAR_STATUS.DISCONNECTED) return '/content/plugin-widget-gmaps/grey-car.png'

    return ''
}

export const getBlockedStatusIcon = (isBlocked: any) => {
    if (isBlocked == '1') return 'fas fa-lock'

    return 'fal fa-lock-open'
}

export const getBlockedStatusStyle = (isBlocked: any) => {
    const style: any = { fontSize: '10px' }
    if (isBlocked == '1') style.color = '#D9534F'
    else style.color = '#BAC0CB'

    return style
}

export const CAR_STATUS_ICON = {
    [CAR_STATUS.MOVING]: '/content/plugin-widget-gmaps/green-car.png',
    [CAR_STATUS.STOPPED]: '/content/plugin-widget-gmaps/orange-car.png',
    [CAR_STATUS.DISCONNECTED]: '/content/plugin-widget-gmaps/grey-car.png'
}

export const calcTravelledDistance = (markers: Marker[]) => {
    if (markers && markers.length > 0) {
        let acumulate = 0

        markers.forEach(({ distance = 0 }: Marker, index) => {
            if (index === 0) {
                distance = 0
            }

            acumulate += distance
        })

        return acumulate
    }

    return 0
}

export const calcIntervalDistance = (markers: Marker[]) => {
    let start = 0
    let end = 0
    let acumulate = 0
    const interval: any[] = []

    if (markers === null) {
        throw EXCEPTION_MAP.A0001
    }

    markers.forEach((marker: Marker, index: number) => {
        let { distance = 0 } = marker

        if (index === 0) {
            distance = 0
        }

        acumulate += distance

        if (index > 0) {
            end = acumulate
            interval.push({ start, end, marker })
            start = end
        }
    })

    return interval
}

export const isCollinear = (x1, y1, x2, y2, x3, y3) => {
    if (!x1 || !y1 || !x2 || !y2 || !x3 || !y3) {
        return false
    }

    const result = x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)

    return Math.abs(result) === 0
}

export const mountInformation = async (marker: Marker, lastXTime: number, lastXKm: number) => {
    const duration = moment.duration(lastXTime)
    const hours = duration.hours() < 10 ? '0' + duration.hours() : duration.hours()
    const minutes = duration.minutes() < 10 ? '0' + duration.minutes() : duration.minutes()
    const seconds = duration.seconds() < 10 ? '0' + duration.seconds() : duration.seconds()
    const reverseGeocoding = await getReverseGeocoding([`${marker.lat},${marker.lng}`])
    const geocoding = reverseGeocoding?.data?.[0]?.geocodingResult
    const address = formatAddress(geocoding?.addressComponents, geocoding?.formattedAddress)
    
    return {
        lat: marker.lat,
        lng: marker.lng,
        travelledDistance: lastXKm,
        travelledTime: `${hours}:${minutes}:${seconds}`,
        score: marker.score,
        speed: marker.speed,
        levelColor: marker.levelColor,
        address: address
    }
}

export const objectsEqual = (o1: any, o2: any): boolean => {
    return typeof o1 === 'object' && Object.keys(o1).length > 0
        ? Object.keys(o1).length === Object.keys(o2).length && Object.keys(o1).every(p => objectsEqual(o1[p], o2[p]))
        : o1 === o2
}

export const arraysEqual = (a1: any[], a2: any[]) =>
    a1.length === a2.length && a1.every((o, idx) => objectsEqual(o, a2[idx]))

export const isObjectEmpty = value => {
    return Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}'
}

export const sleep = ms => {
    return new Promise(resolve => setTimeout(resolve, ms))
}

export const getCollection = context => {
    let collection = ''

    if (context?.state?.filterValues) {
        const key = Object.keys(context.state.filterValues)[0]
        collection = context.state.filterValues[key][0]
    } else {
        throw "CAN'T RECOVER COLLECTION'S NAME"
    }

    return collection
}

export const MAP_SHAPE = {
    MARKER: 'MARKER',
    CIRCLE: 'CIRCLE',
    POLYGON: 'POLYGON'
}

export const mockAux = [
    {
        id: 1,
        lat: -22.804365212023246,
        lng: -43.20570920551193,
        levelColor: '#14C997'
    },
    {
        id: 2,
        lat: -22.804340486516402,
        lng: -43.20570920551193,
        levelColor: '#348AC7',
        distance: 10,
        time: 1
    },
    {
        id: 3,
        lat: -22.80432070610771,
        lng: -43.20570920551193,
        levelColor: '#14C997',
        distance: 5,
        time: 0
    },
    {
        id: 4,
        lat: -22.80430587079929,
        lng: -43.20570920551193,
        levelColor: '#348AC7',
        distance: 2.5,
        time: 0
    },
    {
        id: 5,
        lat: -22.80428114528168,
        lng: -43.20570920551193,
        levelColor: '#14C997',
        distance: 2.5,
        time: 0
    },
    {
        id: 6,
        lat: -22.804261364864377,
        lng: -43.2052532299794,
        levelColor: '#348AC7',
        distance: 1,
        time: 0
    },
    {
        id: 7,
        lat: -22.804236639338693,
        lng: -43.20515667045486,
        levelColor: '#14C997',
        distance: 1,
        time: 3600
    }
]

export const HOUR_IN_MILLI = 3600000
